import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { ChartCard, Legend, LineChart, SERIES_COLORS, StatTile } from './ReportCharts';
import { count, formatDateTime, periodLabel } from './reportsFormat';
import { AUDIT_CATEGORIES, auditEventText, categoryColor, staffAuditText } from './opsText';
import useReport from './useReport';
import ReportState from './ReportState';

export default function AuditTab({ days, onFreshness }) {
  const [category, setCategory] = useState('ALL');
  const params = useMemo(() => ({ days, category, limit: 200 }), [days, category]);
  const report = useReport((p) => CoreReportsService.audit(p), params);
  const data = report.data;
  const generatedAt = data?.generatedAt;
  useEffect(() => { onFreshness?.(generatedAt ? { live: true, generatedAt } : null); }, [generatedAt, onFreshness]);

  return (
    <ReportState report={report}>
      {data && <Content data={data} category={category} onCategory={setCategory} />}
    </ReportState>
  );
}

function Content({ data, category, onCategory }) {
  const { failedLogins: fl, events, staffActions } = data;
  const labels = fl.series.map((d) => periodLabel(d.date, 'day'));
  const showEvents = category !== 'STAFF';
  const showStaff = category === 'ALL' || category === 'STAFF';

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6} md={2.4}><StatTile label="Logins fallidos" value={count(fl.failed)} hint="Contraseña o cuenta inválida" /></Grid>
        <Grid item xs={6} md={2.4}><StatTile label="Intentos bloqueados" value={count(fl.blocked)} hint="Rechazados por exceso de fallos" /></Grid>
        <Grid item xs={6} md={2.4}><StatTile label="Dominios desconocidos" value={count(fl.unknownDomain)} hint="Correos sin empresa" /></Grid>
        <Grid item xs={6} md={2.4}><StatTile label="Accesos al admin" value={count(data.coreLogins)} /></Grid>
        <Grid item xs={12} md={2.4}><StatTile label="Fallos en el admin" value={count(data.coreLoginsFailed)} hint="Intentos con cuenta de personal" /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={7}>
          <ChartCard
            title="Logins fallidos por día" subtitle="Fallidos y bloqueados"
            legend={<Legend items={[{ name: 'Fallidos', color: SERIES_COLORS[0] }, { name: 'Bloqueados', color: SERIES_COLORS[1] }]} />}
            table={{ columns: ['Día', 'Fallidos', 'Bloqueados'], rows: fl.series.map((d, i) => [labels[i], count(d.failed), count(d.blocked)]) }}
          >
            <LineChart
              labels={labels} format={(v) => String(Math.round(v))}
              series={[{ name: 'Fallidos', color: SERIES_COLORS[0], values: fl.series.map((d) => d.failed) }, { name: 'Bloqueados', color: SERIES_COLORS[1], values: fl.series.map((d) => d.blocked) }]}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Direcciones IP con más fallos</Typography>
              {fl.topIps.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>No hubo intentos fallidos en el período.</Typography>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead><TableRow><TableCell>IP</TableCell><TableCell align="right">Fallidos</TableCell><TableCell align="right">Bloqueados</TableCell><TableCell align="right">Último</TableCell></TableRow></TableHead>
                  <TableBody>
                    {fl.topIps.map((ip) => (
                      <TableRow key={ip.ip}>
                        <TableCell>{ip.ip}</TableCell><TableCell align="right">{count(ip.failed)}</TableCell><TableCell align="right">{count(ip.blocked)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(ip.lastAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {fl.byCompany.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Empresas con más logins fallidos</Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell align="right">Fallidos</TableCell></TableRow></TableHead>
              <TableBody>
                {fl.byCompany.map((c) => (
                  <TableRow key={c.empresaId}><TableCell>{c.empresaName || `Empresa #${c.empresaId}`}</TableCell><TableCell align="right">{count(c.failed)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, mb: 1 }}>
        <Typography variant="h6">Registro de actividad</Typography>
        <TextField size="small" select label="Mostrar" value={category} onChange={(e) => onCategory(e.target.value)} sx={{ minWidth: 220 }}>
          {AUDIT_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Los correos de los logins fallidos se guardan enmascarados (por ejemplo a***@g***.com): sirven para ver patrones sin exponer a la persona. Nunca se guardan contraseñas.
      </Alert>

      {showEvents && (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Fecha</TableCell><TableCell>Tipo</TableCell><TableCell>Evento</TableCell><TableCell>Quién</TableCell><TableCell>Empresa</TableCell><TableCell>IP</TableCell><TableCell>Detalle</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No hay eventos en el período.</TableCell></TableRow>}
              {events.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={categoryColor(e.category)} label={e.category === 'SECURITY' ? 'Seguridad' : 'Configuración'} /></TableCell>
                  <TableCell>{auditEventText(e.action)}</TableCell>
                  <TableCell>{e.actorEmail || e.subject || '-'}</TableCell>
                  <TableCell>{e.empresaName || (e.empresaId ? `#${e.empresaId}` : '-')}</TableCell>
                  <TableCell>{e.ip || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.detail || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showStaff && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Acciones sobre el personal del admin</Typography>
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Quién</TableCell><TableCell>Acción</TableCell><TableCell>Sobre</TableCell><TableCell>Detalle</TableCell></TableRow></TableHead>
              <TableBody>
                {staffActions.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No hay acciones en el período.</TableCell></TableRow>}
                {staffActions.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.at)}</TableCell>
                    <TableCell>{a.actorEmail}</TableCell>
                    <TableCell>{staffAuditText(a.action)}</TableCell>
                    <TableCell>{a.targetEmail || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.detail || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
